// src/utils/policySelect.js
//
// Pure helpers for choosing policies now that FIG_Policies carries prior-term
// history rows (Status "Expired"). "Current" means Status Active or Bound,
// case-insensitive. Every date comparison goes through parseLocalDate; a date
// that fails to parse sorts last and is never treated as zero.

import { parseLocalDate } from './parseLocalDate';

const CURRENT_STATUSES = new Set(['active', 'bound']);

export function isCurrentPolicy(p) {
  const s = String(p?.Status ?? '').trim().toLowerCase();
  return CURRENT_STATUSES.has(s);
}

function effectiveTime(p) {
  const d = parseLocalDate(p?.EffectiveDate);
  return d ? d.getTime() : null;
}

// Newest EffectiveDate first. Unparseable/blank dates go last. Stable among ties.
export function sortByEffectiveDesc(policies) {
  return (policies || [])
    .map((p, i) => ({ p, i, t: effectiveTime(p) }))
    .sort((a, b) => {
      if (a.t === null && b.t === null) return a.i - b.i;
      if (a.t === null) return 1;
      if (b.t === null) return -1;
      if (b.t !== a.t) return b.t - a.t;
      return a.i - b.i;
    })
    .map(x => x.p);
}

// Current rows first (newest first), then everything else (newest first).
export function currentFirst(policies) {
  const list = policies || [];
  return [
    ...sortByEffectiveDesc(list.filter(isCurrentPolicy)),
    ...sortByEffectiveDesc(list.filter(p => !isCurrentPolicy(p))),
  ];
}

// Newest current row whose LOB matches (case-insensitive, trimmed), else null.
export function pickCurrentForLOB(policies, lob) {
  const want = String(lob ?? '').trim().toLowerCase();
  if (!want) return null;
  const matches = (policies || []).filter(
    p => isCurrentPolicy(p) && String(p.LOB ?? '').trim().toLowerCase() === want
  );
  return sortByEffectiveDesc(matches)[0] || null;
}
